import { useMemo, useState } from 'react'

export type PaginationState<T> = {
  page: number
  pageSize: number
  pageItems: T[]
  totalItems: number
  totalPages: number
  setPage: (p: number) => void
  setPageSize: (s: number) => void
}

/** Hook: dado un array, devuelve el slice de la pagina actual + controles. */
export function usePagination<T>(items: T[], defaultPageSize = 20): PaginationState<T> {
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [page, setPageRaw] = useState(1)

  const totalItems = items.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))

  // Clamp: si el total cambia (re-filtrado) y la pagina actual queda fuera, volvemos a 1.
  const clampedPage = Math.min(page, totalPages)

  const pageItems = useMemo(() => {
    const start = (clampedPage - 1) * pageSize
    return items.slice(start, start + pageSize)
  }, [items, clampedPage, pageSize])

  const setPage = (p: number) => setPageRaw(Math.max(1, Math.min(p, totalPages)))

  return {
    page: clampedPage,
    pageSize,
    pageItems,
    totalItems,
    totalPages,
    setPage,
    setPageSize: (s) => {
      setPageSize(s)
      setPageRaw(1)
    },
  }
}

/** Barra de controles de paginacion. Muestra rangos y navegacion compacta. */
export function PaginationBar<T>({ state }: { state: PaginationState<T> }) {
  const { page, pageSize, totalItems, totalPages, setPage, setPageSize } = state

  if (totalItems === 0) return null

  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalItems)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderTop: '1px solid var(--n-100)',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ color: 'var(--n-500)', fontSize: 12 }}>
        Mostrando <strong>{start}</strong>–<strong>{end}</strong> de{' '}
        <strong>{totalItems}</strong>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ color: 'var(--n-500)', fontSize: 12 }}>
          Por página:&nbsp;
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{
              border: '1px solid var(--n-200)',
              background: 'var(--surface)',
              padding: '2px 6px',
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>

        <div style={{ display: 'inline-flex', gap: 4 }}>
          <button
            className="btn btn-ghost"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            ‹ Anterior
          </button>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 10px',
              fontSize: 12,
              color: 'var(--n-700)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {page} / {totalPages}
          </span>
          <button
            className="btn btn-ghost"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            Siguiente ›
          </button>
        </div>
      </div>
    </div>
  )
}
